import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/input';
import Button from '../../../components/ui/button';
import Icon from '../../../components/appIcon';
import PasswordStrengthIndicator from './passwordStrengthIndicator';
import axiosInstance from 'utils/axiosInstance';


const ResetPasswordForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({ new: false, confirm: false });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/\d/.test(password)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return 'Password must contain at least one special character';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.otp) newErrors.otp = 'OTP is required';
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) newErrors.newPassword = passwordError;
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const data = new FormData();
      data.append('otp', formData.otp);
      data.append('new_password', formData.newPassword);

      await axiosInstance.post('/employee/reset-password', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      onSuccess(); // show success message
    } catch (error) {
      setErrors({ submit: error.response?.data?.detail || 'Failed to reset password. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* OTP Field */}
      <Input
        label="OTP"
        type="text"
        name="otp"
        value={formData.otp}
        onChange={handleInputChange}
        placeholder="Enter the OTP sent to your email"
        error={errors.otp}
        required
        disabled={isLoading}
      />

      {/* New Password */}
      <div className="relative">
        <Input
          label="New Password"
          type={showPassword.new ? 'text' : 'password'}
          name="newPassword"
          value={formData.newPassword}
          onChange={handleInputChange}
          placeholder="Enter your new password"
          error={errors.newPassword}
          required
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={() => togglePasswordVisibility('new')}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
          disabled={isLoading}
        >
          <Icon name={showPassword.new ? 'EyeOff' : 'Eye'} size={18} />
        </button>
      </div>

      {/* Password Strength */}
      <PasswordStrengthIndicator password={formData.newPassword} isVisible={formData.newPassword.length > 0} />

      {/* Confirm Password */}
      <div className="relative">
        <Input
          label="Confirm New Password"
          type={showPassword.confirm ? 'text' : 'password'}
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          placeholder="Confirm your new password"
          error={errors.confirmPassword}
          required
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={() => togglePasswordVisibility('confirm')}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
          disabled={isLoading}
        >
          <Icon name={showPassword.confirm ? 'EyeOff' : 'Eye'} size={18} />
        </button>
      </div>

      {errors.submit && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center space-x-2">
          <Icon name="AlertCircle" size={16} className="text-error" />
          <span className="text-sm text-error">{errors.submit}</span>
        </div>
      )}

      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        disabled={!formData.otp || !formData.newPassword || !formData.confirmPassword}
        iconName="Lock"
        iconPosition="left"
      >
        {isLoading ? 'Resetting Password...' : 'Reset Password'}
      </Button>
    </form>
  );
};

export default ResetPasswordForm;
